'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePreventionTargetMappingBody,
  PreventionWafVendor,
} from '@terra/shared-contracts-integrations';
import { terraProxyApiClient } from '../../clients/testerClient';

export const WAF_PREVENTION_CONNECTIONS_KEY = 'waf-prevention-connections';
export const AWS_CLOUD_CONNECTION_STATUS_KEY = 'aws-cloud-connection-status';
export const APPLICATION_PREVENTION_MAPPINGS_KEY =
  'application-prevention-mappings';
export const APPLICATION_WAF_SUGGESTIONS_KEY = 'application-waf-suggestions';
export const PREVENTION_WAF_TARGETS_KEY = 'prevention-waf-targets';

const EMPTY_LIST: never[] = [];

export function useWafPreventionConnections({ enabled }: { enabled: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [WAF_PREVENTION_CONNECTIONS_KEY],
    queryFn: () => terraProxyApiClient.listWafPreventionConnections(),
    enabled,
  });

  return { connections: data ?? EMPTY_LIST, isLoading: enabled && isLoading };
}

export function useAwsCloudConnectionStatus({ enabled }: { enabled: boolean }) {
  const { data } = useQuery({
    queryKey: [AWS_CLOUD_CONNECTION_STATUS_KEY],
    queryFn: () => terraProxyApiClient.getAwsCloudConnectionStatus(),
    enabled,
  });

  return { connectionStatus: data ?? null };
}

export function useApplicationPreventionMappings({
  applicationId,
  enabled,
}: {
  applicationId: string;
  enabled: boolean;
}) {
  const { data, refetch } = useQuery({
    queryKey: [APPLICATION_PREVENTION_MAPPINGS_KEY, applicationId],
    queryFn: () =>
      terraProxyApiClient.listApplicationPreventionMappings(applicationId),
    enabled: enabled && Boolean(applicationId),
  });

  return { mappings: data ?? EMPTY_LIST, refetch };
}

export function useApplicationWafSuggestions({
  applicationId,
  enabled,
}: {
  applicationId: string;
  enabled: boolean;
}) {
  const { data, isLoading } = useQuery({
    queryKey: [APPLICATION_WAF_SUGGESTIONS_KEY, applicationId],
    queryFn: async () => {
      const { suggestions } =
        await terraProxyApiClient.getApplicationWafSuggestions(applicationId);

      return suggestions;
    },
    enabled: enabled && Boolean(applicationId),
  });

  return { suggestions: data ?? EMPTY_LIST, isLoading: enabled && isLoading };
}

export function usePreventionWafTargets({
  vendor,
  connectionId,
  enabled,
}: {
  vendor: PreventionWafVendor;
  connectionId: string | undefined;
  enabled: boolean;
}) {
  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: [PREVENTION_WAF_TARGETS_KEY, vendor, connectionId ?? null],
    queryFn: () =>
      terraProxyApiClient.discoverPreventionWafTargets({
        vendor,
        connectionId,
      }),
    enabled,
    staleTime: 60_000,
  });

  return {
    targets: data?.targets ?? EMPTY_LIST,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
}

export function usePreventionMappingMutations({
  applicationId,
}: {
  applicationId: string;
}) {
  const queryClient = useQueryClient();
  const invalidateMappings = () =>
    queryClient.invalidateQueries({
      queryKey: [APPLICATION_PREVENTION_MAPPINGS_KEY, applicationId],
    });
  const createMapping = useMutation({
    mutationFn: (body: CreatePreventionTargetMappingBody) =>
      terraProxyApiClient.createPreventionTargetMapping(body),
    onSuccess: invalidateMappings,
  });
  const deleteMapping = useMutation({
    mutationFn: ({ mappingId }: { mappingId: string }) =>
      terraProxyApiClient.deletePreventionTargetMapping(mappingId),
    onSuccess: invalidateMappings,
  });
  const ensureAwsWafConnection = useMutation({
    mutationFn: () => terraProxyApiClient.ensureAwsWafPreventionConnection(),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [WAF_PREVENTION_CONNECTIONS_KEY],
      }),
  });

  return { createMapping, deleteMapping, ensureAwsWafConnection };
}
